import { StyledDrawer, StyledDrawerTitle, StyledFooter } from './style';
import { Tabs } from '../../Tabs';
import { useIntl } from 'react-intl';
import {
  NewProductForm,
  ProducerLookupParams,
} from '../../Form/products/NewProductForm';
import { Button } from '../../Button';
import { NewProductGallery } from '../../Form/products/NewProductGallery';
import { NewProductMacros } from '../../Form/products/NewProductMacros';
import { NewProductDeliveryForm } from '../../Form/products/NewProductDeliveryForm';
import React, { useState } from 'react';
import { Flex, Form, message } from 'antd';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { NewProductsStructureForm } from '../../Form/products/NewProductsStructureForm';
import { UploadFile } from 'antd/es/upload/interface';
import { Spinner } from '../../Spinner';
import { getMimeType } from '../../../utils/imageHelpers';
import { Icon } from 'ui/Icon';
import { InfrastructureProps } from 'interfaces/BackendModels';
import { MansLauksProduct } from './DrawerMansLauksProducts';
import { ProductData, QualityMarkProductData } from 'types/ProductData';
import { RcFile } from 'antd/lib/upload';
import { useAuth } from 'hooks/useAuth';
import { NewProductRetail } from '../../Form/products/NewProductRetail';
import {
  NewProductWholesale,
  WholesalePriceRangeProps,
  WholesaleReserveProps,
  isWholesaleRowValueFilled,
} from '../../Form/products/NewProductWholesale';
import { Checkbox } from 'ui';
import dayjs from 'dayjs';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';

export interface FarmProps {
  id: number;
  name: string;
}

interface DrawerCreateProductProps {
  farmId?: number | null;
  open?: boolean;
  onClose?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  closeDrawer?: (value: boolean) => void;
  productId?: number;
  setReload: React.Dispatch<React.SetStateAction<number>>;
  mansLauksProducts: MansLauksProduct[];
  products: ProductData[];
  farms: FarmProps[];
}

export interface CategoryOption {
  id: number;
  label: string;
}

export interface ProductAmountProps {
  type: string;
}

export const DrawerCreateProduct = ({
  open,
  onClose,
  productId,
  closeDrawer,
  farmId,
  setReload,
  mansLauksProducts,
  products,
  farms,
}: DrawerCreateProductProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [adminFarmId, setAdminFarmId] = useState<number | null | undefined>(
    farmId
  );
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedImageName, setSelectedImageName] = useState<string>();
  const [mainCategoryValue, setMainCategoryValue] = useState<
    number | undefined
  >();
  const [subCategoryValue, setSubCategoryValue] = useState<
    number | undefined
  >();
  const [subSubCategoryValue, setSubSubCategoryValue] = useState<
    number | undefined
  >();
  const [isEmarket, setIsEmarket] = useState<boolean>(false);
  const [isWholesale, setIsWholesale] = useState<boolean>(false);
  const [initialProducerLookup, setInitialProducerLookup] =
    useState<ProducerLookupParams | null>(null);

  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const intl = useIntl();
  const { user } = useAuth();

  const isAdmin = user?.isAdmin;
  const hasEmarketContract =
    isAdmin || (user?.farm?.has_emarket_contract ?? false);
  const hasWholesaleContract =
    isAdmin || (user?.farm?.has_wholesale_contract ?? false);

  const isEditMode = !!productId;
  const requestUrl = isEditMode
    ? `api/v2/products/${productId}?_method=PATCH`
    : 'api/v2/products';

  const product = products.find((product) => product.id === productId);

  const mansLauksSelectedProduct = mansLauksProducts.find(
    (mlProd) =>
      mlProd.id === product?.manslauks_id &&
      mlProd.bio === product?.manslauks_bio
  );

  const title = (
    <StyledDrawerTitle>
      {isEditMode
        ? intl.formatMessage({ id: 'general.edit_product_title' })
        : intl.formatMessage({ id: 'general.create_product_title' })}
      <Form form={form2}>
        <Flex gap={20}>
          <Checkbox
            label={intl.formatMessage({ id: 'general.visible' })}
            name="hidden"
            initialValue={!isEditMode ? true : undefined}
          />
          <Checkbox
            label={intl.formatMessage({ id: 'general.gmo' })}
            name="is_processed"
          />
          <Checkbox
            label={intl.formatMessage({ id: 'general.e-market' })}
            name="emarket"
            onChange={(e) => setIsEmarket(e.target.checked)}
            disabled={!hasEmarketContract}
          />
          <Checkbox
            label={intl.formatMessage({ id: 'general.wholesale' })}
            name="is_wholesale"
            onChange={(e) => setIsWholesale(e.target.checked)}
            disabled={!hasWholesaleContract}
          />
        </Flex>
      </Form>
    </StyledDrawerTitle>
  );

  const { appendData: callImage, isLoading: imageIsLoading } =
    useQueryApiClient({
      request: {
        url: '/api/v2/image',
        multipart: true,
        disableOnMount: true,
      },
      onSuccess: (response, passOnSuccess) => {
        handleRawFile(
          response,
          passOnSuccess.path,
          passOnSuccess.id,
          passOnSuccess.index
        );
      },
    });

  const { isLoading } = useQueryApiClient({
    request: {
      url: `api/v2/products/${productId}`,
      data: {
        asResource: true,
      },
      disableOnMount: !isEditMode,
    },
    onSuccess: (response) => handleProductForm(response),
  });

  const { appendData, isLoading: saveIsLoding } = useQueryApiClient({
    request: {
      url: requestUrl,
      method: 'POST',
      multipart: true,
    },
    onSuccess: () => {
      handleSuccess();
    },
    onError: (response) => {
      const firstError = response?.errors
        ? (Object.values(response.errors).flat()[0] as string)
        : (response?.error ?? response?.message);

      message.error(
        firstError && intl.messages[firstError]
          ? intl.formatMessage({ id: firstError })
          : firstError || intl.formatMessage({ id: 'validation.error' })
      );
    },
  });

  const handleRawFile = (
    imageRawFile: Blob,
    imageUrl: string,
    id: string,
    index: number
  ) => {
    const urlParts = imageUrl.split('/');
    const fileName = urlParts[urlParts.length - 1] || 'image.png';

    const mimeType = getMimeType(fileName);

    const rcFile = new File([imageRawFile], fileName, {
      type: mimeType,
    }) as RcFile;

    const uploadFile: UploadFile = {
      lastModified: rcFile.lastModified,
      lastModifiedDate: new Date(rcFile.lastModified),
      name: rcFile.name,
      status: 'done',
      url: URL.createObjectURL(rcFile),
      uid: id,
      originFileObj: rcFile,
      type: rcFile.type,
      preview: URL.createObjectURL(rcFile),
    };

    let parsed = fileList;
    parsed[index] = uploadFile;

    setFileList(parsed);
  };

  const handleProductForm = (productData: any) => {
    const currentValues = form.getFieldsValue();

    const wholesale = productData.product_amounts.find(
      (amount: ProductAmountProps) => amount.type === 'WHOLESALE'
    );

    form.setFieldsValue({
      ...productData,
      nutritional_values: {
        ...currentValues.nutritional_values,
        kcal: productData?.nutritional_values?.kcal ?? null,
        fat: productData?.nutritional_values?.fat ?? null,
        saturated_fat: productData?.nutritional_values?.saturated_fat ?? null,
        carbohydrates: productData?.nutritional_values?.carbohydrates ?? null,
        sugars: productData?.nutritional_values?.sugars ?? null,
        proteins: productData?.nutritional_values?.proteins ?? null,
        salt: productData?.nutritional_values?.salt ?? null,
      },
      retail: {
        ...productData.product_amounts.find(
          (amount: ProductAmountProps) => amount.type === 'EMARKET'
        ),
      },
      wholesale: {
        ...wholesale,
        wholesale_reserves: wholesale?.wholesale_reserves
          ? wholesale.wholesale_reserves.map(
              (reserve: WholesaleReserveProps) => ({
                ...reserve,
                date_from: reserve.date_from
                  ? dayjs(reserve.date_from)
                  : undefined,
                date_to: reserve.date_to ? dayjs(reserve.date_to) : undefined,
              })
            )
          : [],
      },
    });

    form2.setFieldsValue({
      emarket: productData.emarket,
      hidden: !productData.hidden,
      is_processed: productData.is_processed,
      is_wholesale: productData.wholesale,
    });

    setIsEmarket(productData.emarket);
    setIsWholesale(productData.wholesale);

    if (productData.image) {
      const filename = productData.image.split('/').pop();
      setSelectedImageName(filename);
    }

    if (productData['main-category']) {
      setMainCategoryValue(Number(productData['main-category']));
    }

    if (productData['sub-category']) {
      setSubCategoryValue(Number(productData['sub-category']));
    }

    if (productData['sub-subcategory']) {
      setSubSubCategoryValue(Number(productData['sub-subcategory']));
    }

    if (productData['options']) {
      const selectedOptions: number[] = productData['options'].map(
        (opt: CategoryOption) => opt.id
      );
      form.setFieldsValue({ options: selectedOptions });
    }

    if (productData['qualitymarks']) {
      const selectedQmarks: number[] = (
        productData['qualitymarks'] as QualityMarkProductData[]
      ).map((qmark: QualityMarkProductData) => qmark.quality_mark_id);
      form.setFieldsValue({ qmarks: selectedQmarks });
    }

    form.setFieldsValue({
      producer_qmarks: productData.producer_qmark_uids ?? [],
    });

    setInitialProducerLookup(
      productData.producer_reg_nr
        ? {
            reg_nr: productData.producer_reg_nr,
            person_full_name: productData.producer_name ?? undefined,
          }
        : { farm_id: productData.farm_id }
    );

    const farmInfrastructures: InfrastructureProps[] =
      productData.farm_infrastructures;

    farmInfrastructures.forEach((infrastructure) => {
      form.setFieldValue(
        [
          'infrastructures',
          infrastructure.pivot.farm_infrastructure_id.toString(),
          'emarket',
        ],
        infrastructure.pivot?.['farm_infrastructures_products.emarket']
      );
      form.setFieldValue(
        [
          'infrastructures',
          infrastructure.pivot.farm_infrastructure_id.toString(),
          'wholesale',
        ],
        infrastructure.pivot?.['farm_infrastructures_products.wholesale']
      );
    });

    if (productData['product_deliveries']) {
      productData['product_deliveries'].forEach((delivery: any) => {
        form.setFieldValue(
          ['deliveries', delivery.farm_delivery_id.toString(), 'emarket'],
          delivery.emarket
        );
      });
    }

    if (productData.images && Array.isArray(productData.images)) {
      productData.images.forEach((img: any, index: number) => {
        callImage({ path: img.url_original }, [], {
          path: img.url_original,
          id: img.id,
          index: index,
        });
      });
    }
  };

  const handleSuccess = () => {
    resetEverything();
    if (closeDrawer) {
      closeDrawer(false);
    }
    setReload((old) => old + 1);
  };

  const stepFieldMap: any = {
    add_product: [
      'name',
      'main-category',
      'sub-category',
      'sub-subcategory',
      'realisation_time',
      'producer_reg_nr',
      'producer_person_name',
      'producer_person_surname',
      'producer_qmarks',
      'description',
    ],
    add_retail: [
      ['retail', 'storage_unit'],
      ['retail', 'step'],
      ['retail', 'storage_stock_count'],
      ['retail', 'storage_stock_price'],
      ['retail', 'storage_stock_discount_price'],
      ['retail', 'storage_limit_max'],
      ['retail', 'storage_limit_min'],
      ['retail', 'availability'],
      ['retail', 'package_unit'],
      ['retail', 'package_unit_val'],
    ],
    add_wholesale: [
      ['wholesale', 'storage_unit'],
      ['wholesale', 'step'],
      ['wholesale', 'storage_stock_count'],
      ['wholesale', 'storage_stock_price'],
      ['wholesale', 'storage_stock_discount_price'],
      ['wholesale', 'storage_limit_max'],
      ['wholesale', 'storage_limit_min'],
      ['wholesale', 'package_unit'],
      ['wholesale', 'package_unit_val'],
    ],
    add_product_infrastructure: [],
    add_product_image: [],
    add_products_delivery: [],
    add_product_macros: [],
  };

  const steps = [
    {
      key: 'add_product',
      label: intl.formatMessage({ id: 'navigation.information' }),
      content: (
        <NewProductForm
          mainCategoryValue={mainCategoryValue}
          subCategoryValue={subCategoryValue}
          subSubCategoryValue={subSubCategoryValue}
          productId={productId}
          farmId={farmId ?? user?.farmId}
          adminFarmId={adminFarmId}
          initialProducerLookup={initialProducerLookup}
          farms={farms}
          isEditMode={isEditMode}
          setAdminFarmId={setAdminFarmId}
        />
      ),
    },
    {
      key: 'add_retail',
      label: intl.formatMessage({ id: 'navigation.retail' }),
      content: (
        <NewProductRetail mansLauksSelectedProduct={mansLauksSelectedProduct} />
      ),
    },
    {
      key: 'add_wholesale',
      label: intl.formatMessage({ id: 'navigation.wholesale' }),
      content: (
        <NewProductWholesale
          mansLauksSelectedProduct={mansLauksSelectedProduct}
        />
      ),
    },
    {
      key: 'add_product_infrastructure',
      label: intl.formatMessage({ id: 'navigation.infrastructure' }),
      content: (
        <NewProductsStructureForm
          farmId={farmId ?? user?.farmId}
          defaultChecked={!isEditMode}
        />
      ),
    },
    {
      key: 'add_product_image',
      label: intl.formatMessage({ id: 'navigation.gallery' }),
      content: (
        <NewProductGallery
          onFileListChange={setFileList}
          fileList={fileList}
          selectedImageName={selectedImageName}
          setSelectedImageName={setSelectedImageName}
        />
      ),
    },
    {
      key: 'add_product_macros',
      label: intl.formatMessage({ id: 'navigation.macros' }),
      content: <NewProductMacros />,
    },
    {
      key: 'add_products_delivery',
      label: intl.formatMessage({ id: 'navigation.deliveries' }),
      content: (
        <NewProductDeliveryForm
          farmId={adminFarmId ?? farmId ?? user?.farmId}
          defaultChecked={!isEditMode}
        />
      ),
    },
  ].filter((e) => {
    if (e.key === 'add_retail' && (!isEmarket || !hasEmarketContract)) {
      return false;
    }

    if (e.key === 'add_wholesale' && (!isWholesale || !hasWholesaleContract)) {
      return false;
    }

    return true;
  });

  const nextStep = async () => {
    try {
      await validateFields();

      setCurrentStep(currentStep + 1);
    } catch (error: unknown) {
      handleErrors(error);
    }
  };

  function isValidationError(
    error: unknown
  ): error is { errorFields: { name: string[] }[] } {
    return (
      typeof error === 'object' && error !== null && 'errorFields' in error
    );
  }

  const resetEverything = () => {
    form.resetFields();
    setCurrentStep(0);
    setMainCategoryValue(undefined);
    setSubCategoryValue(undefined);
    setSubSubCategoryValue(undefined);
    setFileList([]);
  };

  const handleClose = (e: React.MouseEvent | React.KeyboardEvent) => {
    resetEverything();
    if (onClose) {
      onClose(e);
    }
  };

  const handleErrors = (error: unknown) => {
    if (isValidationError(error)) {
      if (error.errorFields.length > 1) {
        message.error(
          intl.formatMessage({
            id: 'validation.fix_errors',
          })
        );
      } else {
        const fieldError = error.errorFields[0];
        message.error(
          intl.formatMessage({
            id: `validation.${fieldError.name[0]}`,
          })
        );
      }
    } else {
      message.error(intl.formatMessage({ id: 'validation.error' }));
    }
  };

  const validateFields = async () => {
    const currentStepKey = steps[currentStep].key;
    const fieldsToValidate = stepFieldMap[currentStepKey];

    await form.validateFields(fieldsToValidate);
  };

  const changeStep = async (desiredStep: string) => {
    try {
      await validateFields();

      if (isEditMode) {
        const index = steps.findIndex((step) => step.key === desiredStep);

        if (index === -1) {
          return;
        }
        setCurrentStep(index);
      }
    } catch (error: unknown) {
      handleErrors(error);
    }
  };

  const prevStep = async () => {
    try {
      await validateFields();
      setCurrentStep(currentStep - 1);
    } catch (error: unknown) {
      handleErrors(error);
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const formData = new FormData();
      const v1 = form.getFieldsValue();
      const v2 = form2.getFieldsValue();

      const values = {
        ...v1,
        ...v2,
      };

      const hasTicked = (tabValues: unknown, market: 'emarket' | 'wholesale') =>
        Object.values(tabValues ?? {}).some(
          (row: any) => row?.[market] === true
        );

      if (values.emarket && hasEmarketContract) {
        if (!hasTicked(values.deliveries, 'emarket')) {
          throw new Error(
            intl.formatMessage({ id: 'validation.emarket_requires_delivery' })
          );
        }

        if (!hasTicked(values.infrastructures, 'emarket')) {
          throw new Error(
            intl.formatMessage({
              id: 'validation.emarket_requires_infrastructure',
            })
          );
        }
      }

      if (values.is_wholesale && hasWholesaleContract) {
        if (!hasTicked(values.infrastructures, 'wholesale')) {
          throw new Error(
            intl.formatMessage({
              id: 'validation.wholesale_requires_infrastructure',
            })
          );
        }
      }

      const newImages = fileList.filter((file: any) => file?.originFileObj);

      if (newImages.length) {
        newImages.forEach((file: any) => {
          formData.append('images[]', file?.originFileObj, file?.name);
        });
      } else {
        throw new Error(
          intl.formatMessage({ id: 'validation.image_upload_required' })
        );
      }

      if (selectedImageName) {
        formData.append('selected-image', selectedImageName);
      } else {
        throw new Error(intl.formatMessage({ id: 'error.select_image' }));
      }

      Object.keys(values).forEach((key) => {
        if (key === 'images') return;
        if (key === 'qmark') return;
        if (key === 'producer_qmarks') return;

        const value = values[key];
        if (value === null || value === undefined) {
          return;
        }

        switch (key) {
          case 'main-category':
          case 'sub-category':
          case 'sub-subcategory':
            if (Object.prototype.hasOwnProperty.call(value, 'value')) {
              formData.append(key, value.value);
            } else {
              formData.append(key, value);
            }
            return;
          case 'wholesale':
            value.wholesale_price_ranges =
              value?.wholesale_price_ranges?.filter(
                (range: WholesalePriceRangeProps) =>
                  isWholesaleRowValueFilled(range?.amount_from) ||
                  isWholesaleRowValueFilled(range?.amount_to) ||
                  isWholesaleRowValueFilled(range?.price)
              );

            value.wholesale_reserves = value?.wholesale_reserves
              ?.filter(
                (reserve: WholesaleReserveProps) =>
                  isWholesaleRowValueFilled(reserve?.amount_from) ||
                  isWholesaleRowValueFilled(reserve?.amount_to) ||
                  isWholesaleRowValueFilled(reserve?.date_from) ||
                  isWholesaleRowValueFilled(reserve?.date_to)
              )
              .map((reserve: WholesaleReserveProps) => ({
                ...reserve,
                date_from:
                  reserve?.date_from?.format('YYYY-MM-DD')! ??
                  reserve.date_from,
                date_to:
                  reserve?.date_to?.format('YYYY-MM-DD')! ?? reserve.date_to,
              }));

            formData.append(key, JSON.stringify(value));
            return;
          default:
            if (Array.isArray(value)) {
              value.forEach((value) => formData.append(`${key}[]`, value));
            } else if (typeof value === 'object') {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
            return;
        }
      });

      formData.set(
        'producer_qmarks',
        JSON.stringify(values.producer_qmarks ?? [])
      );

      const resolvedFarmId = isAdmin
        ? (values.farm_id?.value ?? values.farm_id ?? farmId ?? user?.farmId)
        : (farmId ?? user?.farmId);

      if (resolvedFarmId !== null && resolvedFarmId !== undefined) {
        formData.set('farm_id', `${resolvedFarmId}`);
      } else if (!isEditMode) {
        throw new Error(intl.formatMessage({ id: 'validation.farm_required' }));
      }

      appendData(formData);
    } catch (error: unknown) {
      if (isValidationError(error)) {
        handleErrors(error);
      } else if (error instanceof Error) {
        message.error(error.message);
      }
    }
  };

  const MobileFooter = () => (
    <StyledFooter>
      {currentStep > 0 && !isEditMode && (
        <Button
          className={'add-edit-product-button white'}
          label={intl.formatMessage({ id: 'general.previous' })}
          onClick={prevStep}
        />
      )}
      {currentStep === steps.length - 1 && (
        <Button
          className={'add-edit-product-button black'}
          label={intl.formatMessage({ id: 'general.publish' })}
          type={'primary'}
          onClick={handleSubmit}
        />
      )}
      {currentStep < steps.length - 1 && !isEditMode && (
        <Button
          className={'add-edit-product-button black'}
          label={intl.formatMessage({ id: 'general.next_step' })}
          type={'primary'}
          onClick={nextStep}
        />
      )}
      {isEditMode && (
        <Button
          className={'add-edit-product-button white'}
          label={intl.formatMessage({ id: 'general.cancel' })}
          onClick={handleClose}
        />
      )}
      {isEditMode && (
        <Button
          className={'add-edit-product-button black'}
          label={intl.formatMessage({ id: 'general.save' })}
          type={'primary'}
          onClick={handleSubmit}
        />
      )}
    </StyledFooter>
  );

  const Footer = () => (
    <StyledFooter>
      <div className={'btns'}>
        <Button
          className={'add-edit-product-button white'}
          label={intl.formatMessage({ id: 'general.cancel' })}
          onClick={handleClose}
        />
        {currentStep > 0 && (
          <Button
            className={'add-edit-product-button white'}
            label={intl.formatMessage({ id: 'general.previous' })}
            onClick={prevStep}
          />
        )}
        {isEditMode && (
          <Button
            className={'add-edit-product-button black'}
            label={intl.formatMessage({ id: 'general.save' })}
            type={'primary'}
            onClick={handleSubmit}
          />
        )}
        {currentStep < steps.length - 1 && (
          <Button
            className={'add-edit-product-button black'}
            label={intl.formatMessage({ id: 'general.next_step' })}
            type={'primary'}
            onClick={nextStep}
          />
        )}
        {currentStep === steps.length - 1 && (
          <Button
            className={'add-edit-product-button black'}
            label={intl.formatMessage({ id: 'general.publish' })}
            type={'primary'}
            onClick={handleSubmit}
          />
        )}
      </div>
    </StyledFooter>
  );

  return (
    <StyledDrawer
      open={open}
      onClose={handleClose}
      footer={
        <ResponsiveWrapper
          desktopComponent={<Footer />}
          mobileComponent={<MobileFooter />}
        />
      }
      title={title}
      width={1000}
      zIndex={1100}
      closeIcon={<Icon name={'global-close-icon'} type={'small'} />}
      destroyOnClose
      className={'create-products-drawer'}
    >
      <Spinner
        spinning={isLoading || saveIsLoding || imageIsLoading}
        dontRender
      >
        <Form form={form} layout={'vertical'}>
          <Tabs
            onChange={(desiredStep) => {
              changeStep(desiredStep);
            }}
            activeKey={steps[currentStep].key}
            items={steps.map((step) => ({
              key: step.key,
              label: step.label,
              children: step.content,
              forceRender: true,
            }))}
          />
        </Form>
      </Spinner>
    </StyledDrawer>
  );
};
